// Platform-agnostic development check
const isDev = (() => {
  // Try direct access for Vite (when bundled)
  try {
    // @ts-ignore - This will work in Vite but not in TypeScript compilation
    return import.meta.env.DEV;
  } catch {
    // fallthrough for non-Vite environments
  }
  
  // React Native - __DEV__ is standard global
  if (typeof globalThis !== 'undefined' && '__DEV__' in globalThis) {
    return (globalThis as any).__DEV__;
  }
  
  // Node.js fallback
  return process.env.NODE_ENV === 'development';
})();

// Resolve base URL with environment overrides for web dev without impacting prod
let resolvedBase = 'https://platform-certi-production.up.railway.app';
let devProxy = false;
try {
  // @ts-ignore - Vite runtime only
  devProxy = (import.meta as any)?.env?.VITE_DEV_PROXY === 'true';
} catch {}

const viteBase = (() => {
  try {
    // @ts-ignore - Vite runtime only
    return (import.meta as any)?.env?.VITE_API_URL as string | undefined;
  } catch {
    return undefined;
  }
})();

// Optional: fallback for non‑Vite environments (guarded to avoid browser ReferenceError)
let nodeBase: string | undefined = undefined;
try {
  // eslint-disable-next-line no-undef
  if (typeof process !== 'undefined' && (process as any)?.env?.VITE_API_URL) {
    // eslint-disable-next-line no-undef
    nodeBase = (process as any).env.VITE_API_URL as string;
  }
} catch {}

// Prefer dev proxy when enabled
if (isDev && devProxy) {
  resolvedBase = '';
} else if (viteBase) {
  resolvedBase = viteBase;
} else if (nodeBase) {
  resolvedBase = nodeBase;
} else if (isDev) {
  // In dev, allow proxying via Vite when enabled
  resolvedBase = 'http://localhost:8080';
}

export const API_CONFIG = {
  BASE_URL: resolvedBase,
  ENDPOINTS: {
    AUTH: {
      LOGIN: '/api/auth/login',
      REGISTER: '/api/auth/register',
      REFRESH: '/api/auth/refresh',
      LOGOUT: '/api/auth/logout',
      FORGOT_PASSWORD: '/api/auth/password-reset',
      RESET_PASSWORD: '/api/auth/password-reset/confirm',
    },
    USER: {
      PROFILE: '/api/v1/users/{userId}/profile',
      UPDATE_PROFILE: '/api/v1/users/{userId}/profile',
      UPDATE_EMAIL: '/api/v1/users/{userId}/email',
      UPDATE_PASSWORD: '/api/v1/users/{userId}/password',
      STATISTICS: '/api/v1/users/{userId}/statistics',
      BIDDING_ELIGIBILITY: '/api/v1/users/{userId}/bidding-eligibility',
      DELETE_ACCOUNT: '/api/v1/users/{userId}',
    },
    FAVORITES: {
      BASE: '/api/v1/users/{userId}/favorites',
      TOGGLE: '/api/v1/users/{userId}/favorites/toggle',
      CHECK: '/api/v1/users/{userId}/favorites/check',
      BY_TYPE: '/api/v1/users/{userId}/favorites/by-type',
      IDS: '/api/v1/users/{userId}/favorites/ids',
    },
    LISTINGS: {
      PUBLIC: '/api/public/listings',
      PUBLIC_BY_SLUG: '/api/public/listings/{slug}',
      PUBLIC_BY_ID: '/api/public/listings/by-id/{id}',
      ADMIN: '/api/admin/listings',
      ADMIN_BY_ID: '/api/admin/listings/{id}',
      ADMIN_UPDATE_STATUS: '/api/admin/listings/{id}/status',
      MEDIA_UPLOAD_PRESIGNED_URL: '/api/admin/media/upload/presigned-url',
      MEDIA_UPLOAD_CONFIRM: '/api/admin/media/upload/confirm',
      ATTACH_MEDIA: '/api/admin/listings/{id}/media',
    },
  },
  TIMEOUT: 10000, // 10 seconds
};

export const createApiUrl = (endpoint: string): string => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

export const createWebSocketUrl = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (!API_CONFIG.BASE_URL) {
    if (typeof window === 'undefined') {
      return normalizedPath;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}${normalizedPath}`;
  }

  try {
    const baseUrl = new URL(API_CONFIG.BASE_URL);
    const protocol = baseUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${baseUrl.host}${normalizedPath}`;
  } catch {
    if (typeof window === 'undefined') {
      return normalizedPath;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}${normalizedPath}`;
  }
};
